import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import EmotionalLevelGraph from "../Components/EmotionalLevelGraph";
import "./Theme.css";
import "./EmotionalWellbeing.css";

const formatDate = (dateStr) => {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().substr(-2);
  return `${day < 10 ? "0" + day : day}-${month}-${year}`;
};

const EmotionalWellbeing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const action = urlParams.get("action") || "";

  const [apiResponse, setApiResponse] = useState([]);
  const [sortedLogs, setSortedLogs] = useState([]);
  const [text, setText] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - 6 * 24 * 60 * 60 * 1000);
  });
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  const fetchGraph = async () => {
    try {
      const res = await lifestyleApi.fetchEmotionalGraph({ goqiiUserId });
      setText(res.data.text || "");
      if (res.data.data?.moodLogResponse) {
        setApiResponse(res.data.data.moodLogResponse);
      }
    } catch (err) {
      console.error("fetchEmotionalGraph:", err);
    }
  };

  useEffect(() => { fetchGraph(); }, []);

  useEffect(() => {
    if (apiResponse.length > 0) {
      const sorted = [...apiResponse].sort((a, b) => new Date(b.lastUpdatedTime) - new Date(a.lastUpdatedTime));
      const groups = [];
      let current = [];
      sorted.forEach((log, i) => {
        const date = new Date(log.lastUpdatedTime);
        date.setHours(date.getHours() + 5);
        date.setMinutes(date.getMinutes() + 30);
        const logTime = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const lastUpdatedDate = formatDate(log.lastUpdatedTime);
        let commonReasons = "";
        let otherFeelings = "";
        try { commonReasons = JSON.parse(log.commonReason).join(", "); } catch { commonReasons = log.commonReason || ""; }
        try { otherFeelings = JSON.parse(log.anythingElse).join(", "); } catch { otherFeelings = log.anythingElse || ""; }
        const formatted = {
          ...log,
          baseEmotion: log.baseEmotion.charAt(0).toUpperCase() + log.baseEmotion.slice(1),
          commonReasons,
          otherFeelings,
          logTime,
          lastUpdatedDate,
        };
        if (i === 0 || lastUpdatedDate !== formatDate(sorted[i - 1].lastUpdatedTime)) {
          if (current.length > 0) groups.push([...current]);
          current = [];
        }
        current.push(formatted);
        if (i === sorted.length - 1) groups.push([...current]);
      });
      setSortedLogs(groups);
    }
  }, [apiResponse]);

  const qs = `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;
  const addBtnLabel = text === "adhd" ? t("emotionalWellbeing.addMood") : t("emotionalWellbeing.addEmotions");

  return (
    <>
      <section className="ew-section">
        <div className="ew-date-nav">
          <button className="ew-arrow-btn" onClick={() => {
            setStartDate(new Date(startDate.getTime() - 7 * 86400000));
            setEndDate(new Date(endDate.getTime() - 7 * 86400000));
          }}>&#8249;</button>
          <span className="ew-date-range">
            {startDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} to{" "}
            {endDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
          </span>
          <button className="ew-arrow-btn" onClick={() => {
            const today = new Date();
            const ns = new Date(startDate.getTime() + 7 * 86400000);
            const ne = new Date(endDate.getTime() + 7 * 86400000);
            setStartDate(ne > today ? startDate : ns);
            setEndDate(ne > today ? today : ne);
          }}>&#8250;</button>
        </div>

        <h1 className="ew-title">{text === "adhd" ? t("emotionalWellbeing.moodTrend") : t("emotionalWellbeing.emotionalTrend")}</h1>
        <ul className="ew-legend">
          <li><span className="ew-dot" style={{ background: "#4cd964" }}></span> {t("emotionalWellbeing.excellent")}</li>
          <li><span className="ew-dot" style={{ background: "#5ac8fa" }}></span> {t("emotionalWellbeing.good")}</li>
          <li><span className="ew-dot" style={{ background: "#ffcc00" }}></span> {t("emotionalWellbeing.okay")}</li>
          <li><span className="ew-dot" style={{ background: "#ff9500" }}></span> {t("emotionalWellbeing.sad")}</li>
          <li><span className="ew-dot" style={{ background: "#ff3b30" }}></span> {t("emotionalWellbeing.terrible")}</li>
        </ul>
        <EmotionalLevelGraph apiResponses={apiResponse} startDate={startDate} endDate={endDate} text={text} />
      </section>

      {sortedLogs.length > 0 ? (
        sortedLogs.map((group, gi) => (
          <section key={gi} className="ew-log-section">
            {group.map((log, i) => (
              <div key={i}>
                {i === 0 && <div className="ew-log-date">{log.lastUpdatedDate}</div>}
                <div className="ew-log-row">
                  <span className="ew-log-label">{text === "adhd" ? t("emotionalWellbeing.mood") : t("emotionalWellbeing.emotion")}</span>
                  <span className="ew-log-value">{log.baseEmotion}</span>
                </div>
                <div className="ew-log-row">
                  <span className="ew-log-label">{t("emotionalWellbeing.reasons")}</span>
                  <span className="ew-log-value">{log.commonReasons}</span>
                </div>
                <div className="ew-log-row">
                  <span className="ew-log-label">{t("emotionalWellbeing.otherFeelings")}</span>
                  <span className="ew-log-value">{log.otherFeelings}</span>
                </div>
                <div className="ew-log-row">
                  <span className="ew-log-label">{t("emotionalWellbeing.logTime")}</span>
                  <span className="ew-log-value">{log.logTime}</span>
                </div>
              </div>
            ))}
          </section>
        ))
      ) : (
        <section className="ew-log-section">
          <div className="ew-log-row"><span className="ew-log-label">{text === "adhd" ? t("emotionalWellbeing.mood") : t("emotionalWellbeing.emotion")}</span><span className="ew-log-value">---</span></div>
          <div className="ew-log-row"><span className="ew-log-label">{t("emotionalWellbeing.reasons")}</span><span className="ew-log-value">---</span></div>
          <div className="ew-log-row"><span className="ew-log-label">{t("emotionalWellbeing.otherFeelings")}</span><span className="ew-log-value">---</span></div>
          <div className="ew-log-row"><span className="ew-log-label">{t("emotionalWellbeing.logTime")}</span><span className="ew-log-value">---</span></div>
        </section>
      )}

      {action !== "N" && (
        <section className="ew-bottom-btn">
          <button
            type="button"
            className="ew-add-btn"
            onClick={() => navigate(`/mood-tracking?${qs}`)}
          >
            {addBtnLabel}
          </button>
        </section>
      )}
    </>
  );
};

export default EmotionalWellbeing;
